import React from 'react';

// Admin-style interface (React nodes)
interface AdminEmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionButton?: React.ReactNode;
  className?: string;
}

// Dashboard-style interface (action object)
interface DashboardEmptyStateProps {
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

type EmptyStateProps = AdminEmptyStateProps | DashboardEmptyStateProps;

/**
 * EmptyState - Unified empty state component
 * 
 * Supports both admin-style (with icon and actionButton React nodes)
 * and dashboard-style (with action object) interfaces.
 */
const EmptyState: React.FC<EmptyStateProps> = (props) => {
  // Type guard to determine if props are admin-style
  const isAdminStyle = (props: EmptyStateProps): props is AdminEmptyStateProps => {
    return 'icon' in props;
  };

  if (isAdminStyle(props)) {
    const { icon, title, description, actionButton, className = '' } = props;
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
          {icon}
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
        <p className="text-gray-500 mb-4">{description}</p>
        {actionButton}
      </div>
    );
  } else {
    const { title, description, action, className = '' } = props;
    return (
      <div className={`text-center py-12 ${className}`}>
        <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
        <p className="text-gray-500 mb-4">{description}</p>
        {action && (
          <button
            onClick={action.onClick}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {action.label}
          </button>
        )}
      </div>
    );
  }
};

export default EmptyState;
